import { Injectable } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Observable } from 'rxjs';
import { map, distinctUntilChanged, shareReplay } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class DeviceCheckService {
    // observable that emits true when viewport matches handset/small or <= 800px
    public readonly isMobile$: Observable<boolean>;
    // observable that emits true only for phone-sized viewports (< 768px)
    public readonly isPhone$: Observable<boolean>;
    // observable that emits true for desktop viewports (>= 1200px)
    public readonly isDesktop$: Observable<boolean>;
    // Emits true when the viewport is in the lot card's compact range —
    // the SAME <=639px breakpoint as the @media in vb-lot-card's CSS. The
    // lot pages use this to switch to the automatically-triggered compact
    // view (toggle hidden); it is deliberately narrower than isMobile$
    // (<=800px), which gates coarser affordances like paginator density.
    public readonly isCompactCards$: Observable<boolean>;

    private _isMobileSnapshot = false;
    public get isMobileSnapshot(): boolean {
        return this._isMobileSnapshot;
    }

    constructor(private breakpointObserver: BreakpointObserver) {
        this.isMobile$ = this.breakpointObserver
            .observe([
                Breakpoints.Handset,
                Breakpoints.Small,
                '(max-width: 800px)',
            ])
            .pipe(
                map((s: any) => s.matches),
                distinctUntilChanged(),
                shareReplay({ bufferSize: 1, refCount: true }),
            );

        this.isPhone$ = this.breakpointObserver
            .observe(['(max-width: 767px)'])
            .pipe(
                map((s: any) => s.matches),
                distinctUntilChanged(),
                shareReplay({ bufferSize: 1, refCount: true }),
            );

        this.isDesktop$ = this.breakpointObserver
            .observe(['(min-width: 1200px)'])
            .pipe(
                map((s: any) => s.matches),
                distinctUntilChanged(),
                shareReplay({ bufferSize: 1, refCount: true }),
            );

        this.isCompactCards$ = this.breakpointObserver
            .observe(['(max-width: 639px)'])
            .pipe(
                map((s: any) => s.matches),
                distinctUntilChanged(),
                shareReplay({ bufferSize: 1, refCount: true }),
            );

        // keep a local snapshot updated (service is singleton so this subscription lives for app lifetime)
        this.isMobile$.subscribe((v: boolean) => (this._isMobileSnapshot = v));
    }

    async isSlow(): Promise<boolean> {
        if (sessionStorage.getItem('isConnection') === 'slow') {
            return false;
        }
        const conn: any = (navigator as any).connection;
        if (conn?.effectiveType) {
            return ['slow-2g', '2g', '3g'].includes(conn.effectiveType);
        }

        sessionStorage.setItem('isConnection', 'slow');

        const start: any = performance.now();
        try {
            await fetch('/api/users/config/bidder', { cache: 'no-store' });
            if (performance.now() - start < 3000) {
                sessionStorage.removeItem('isConnection');
            }
            return performance.now() - start > 3000;
        } catch {
            return true;
        }
    }
}
